import * as React from 'react'
import { ImagePlus, Loader2, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { uploadService } from '@/services/UploadService'
import { fileToResizedDataUrl } from '@/lib/image'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { ImageDropzone } from '@/components/ImageDropzone'
import { FocalPointPicker } from '@/components/FocalPointPicker'
import { objectPositionOf, type FocalPoint } from '@lib/helpers/focal-point'

/**
 * The longest side a cover is stored at. A cover is displayed far larger than an
 * avatar — full width of a page header — so 256px would be visibly soft, while a
 * source photo straight off a phone is several megabytes. 1600 is enough for a
 * hero on a large display and still lands inside the upload's size limit.
 */
const COVER_MAX_PX = 1600

/**
 * What a frame shows before a photograph exists, per kind of record. A `logo`
 * is a vendor's: a square frame, shown whole rather than cropped, since a
 * logo with its edge cut off is a different logo.
 */
type CoverKind = 'venue' | 'event' | 'vendor' | 'logo'

const EMPTY_COPY: Record<CoverKind, string> = {
  venue: 'Add a photo of this venue',
  event: 'Add a photo for this event',
  vendor: 'Add a photo of your business',
  logo: 'Add your logo',
}

/** A logo is small wherever it is shown; a cover is a page's hero. */
const LOGO_MAX_PX = 800

interface Props {
  /** The current cover, already signed for the browser; null if there is none. */
  coverImageUrl: string | null
  /** Where the subject is. Omit for a caller that does not store one yet. */
  focalPoint?: FocalPoint | null
  /**
   * Persist a focal point. Omitted hides the picker entirely — event and vendor
   * covers do not carry one yet, and a control that silently does nothing is worse
   * than no control.
   */
  onFocalPoint?: (focal: FocalPoint | null) => void
  /** What an unfocused cover uses, matching whatever renders it. */
  unfocusedPosition?: string
  /** What kind of record this is — chooses the empty-state wording. */
  kind: CoverKind
  /**
   * Persist a new cover (a storage key) or clear it with null. The caller owns
   * the request so this control works for a venue, an event or a vendor without
   * knowing which endpoint each uses.
   */
  onSave: (coverImageUrl: string | null) => Promise<void>
  /** Read-only when the viewer may not change this record's photograph. */
  disabled?: boolean
}

/**
 * Pick, replace or remove a record's cover photograph.
 *
 * Previewed in the frame it is shown in — Plan View's 4:3, the record header's
 * shape — so the crop seen here is the crop everyone gets; this used to be a
 * 3:1 banner for a header that no longer exists. With no photograph the frame
 * is the empty well on the plan grid, saying what to add: no drawing stands in,
 * because here someone is deciding whether to upload. Upload resizes in the
 * browser first, so what crosses the wire is bounded whatever the source photo
 * weighs.
 */
export function CoverPhotoField({
  coverImageUrl,
  focalPoint,
  onFocalPoint,
  unfocusedPosition = '50% 45%',
  kind,
  onSave,
  disabled = false,
}: Props) {
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function pick(file: File): Promise<void> {
    setError(null)
    setBusy(true)
    try {
      const dataUrl = await fileToResizedDataUrl(file, kind === 'logo' ? LOGO_MAX_PX : COVER_MAX_PX)
      await onSave(await uploadService.storeImage(dataUrl))
    } catch (e) {
      setError(requestErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function remove(): Promise<void> {
    setError(null)
    setBusy(true)
    try {
      await onSave(null)
    } catch (e) {
      setError(requestErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    // One column the width of the frame, so the photograph, its focal point
    // and the drop target read as one control.
    <div className={cn('space-y-3', kind === 'logo' ? 'max-w-[12rem]' : 'max-w-sm')}>
      <div
        className={cn(
          'relative w-full overflow-hidden rounded-md bg-surface-sunken',
          kind === 'logo' ? 'aspect-square' : 'aspect-[4/3]',
          'after:pointer-events-none after:absolute after:inset-0 after:rounded-md after:shadow-[var(--ds-media-ring)]',
          !coverImageUrl && 'plan-grid',
        )}
      >
        {coverImageUrl ? (
          <img
            src={coverImageUrl}
            alt=""
            className={cn('h-full w-full', kind === 'logo' ? 'bg-surface object-contain p-2' : 'object-cover')}
            style={{ objectPosition: objectPositionOf(focalPoint, unfocusedPosition) }}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 px-4 text-center">
            <ImagePlus className="h-5 w-5 text-ink-subtle" aria-hidden />
            <span className="text-sm font-medium text-ink">{kind === 'logo' ? 'No logo yet' : 'No photo yet'}</span>
            <span className="text-xs text-ink-muted">{EMPTY_COPY[kind]}</span>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center bg-scrim">
            <Loader2 className="h-5 w-5 animate-spin text-on-primary" aria-hidden />
          </div>
        )}
      </div>

      {!disabled && coverImageUrl && onFocalPoint ? (
        <Card className="space-y-2 p-3">
          <p className="text-sm font-medium">Focal point</p>
          <FocalPointPicker
            url={coverImageUrl}
            alt=""
            focalPoint={focalPoint ?? null}
            fallback={unfocusedPosition}
            onChange={onFocalPoint}
            disabled={busy}
          />
        </Card>
      ) : null}

      {!disabled && (
        <div className="space-y-2">
          <ImageDropzone
            onFiles={(files) => {
              const file = files[0]
              if (file) void pick(file)
            }}
            busy={busy}
            disabled={busy}
            label={coverImageUrl ? 'Drag a photo here to replace it' : 'Drag a photo here'}
            hint="Or click to choose one"
          />
          {coverImageUrl && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => void remove()}>
              <Trash2 className="h-4 w-4" aria-hidden />
              Remove
            </Button>
          )}
        </div>
      )}

      {error && <FormError>{error}</FormError>}
    </div>
  )
}
